import React from 'react';

import { faMinus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type Path, type UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { membershipMessages } from 'src/features/memberships/utils/messages';
import { type CreatePricingPlanFormSchemaValues } from 'src/features/pricingPlans/api/useCreatePricingPlan';
import { TIME_TO_LEAVE_OPTIONS } from 'src/features/pricingPlans/types';
import { pricingPlansMessages } from 'src/features/pricingPlans/utils/messages';
import { DialogTitle } from 'src/registry/ui/Dialog';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { Separator } from 'src/registry/ui/Separator';
import { generalMessages, generalPriceMessages } from 'src/utils/messages';

type PricingPlanFormProps = {
    form: UseFormReturn<CreatePricingPlanFormSchemaValues>;
    onSubmit: (values: CreatePricingPlanFormSchemaValues) => void;
};

const formatPrice = (price: number, fractionDigits?: number) => (price / 100).toFixed(fractionDigits || 2);

const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    const formattedHours = String(hours).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');

    return `${formattedHours}:${formattedMinutes}`;
};

enum PRICE {
    VAT = 'vat',
    INITIAL = 'initial_price',
    FIRST_PERIOD = 'first_period.0.due_amount',
    SECOND_PERIOD = 'second_period.0.due_amount',
    THIRD_PERIOD = 'third_period.0.due_amount',
    SPECIAL_PERIOD = 'special_period.0.due_amount',
}
function PricingPlanForm({ onSubmit, form }: PricingPlanFormProps) {
    const { formatMessage } = useIntl();
    const { initial_price, first_period, vat, special_period, second_period, third_period } = form.getValues();
    const [displayedValues, setDisplayedValues] = React.useState<{
        [PRICE.VAT]: string;
        [PRICE.INITIAL]: string;
        [PRICE.FIRST_PERIOD]: string;
        [PRICE.SECOND_PERIOD]: string;
        [PRICE.THIRD_PERIOD]: string;
        [PRICE.SPECIAL_PERIOD]: string;
    }>({
        [PRICE.VAT]: formatPrice(vat, 2),
        [PRICE.INITIAL]: formatPrice(initial_price),
        [PRICE.FIRST_PERIOD]: formatPrice(first_period[0].due_amount),
        [PRICE.SECOND_PERIOD]: formatPrice(second_period[0].due_amount),
        [PRICE.THIRD_PERIOD]: formatPrice(third_period[0].due_amount),
        [PRICE.SPECIAL_PERIOD]: formatPrice(special_period[0].due_amount),
    });

    const timeToLeaveOptions = Object.values(TIME_TO_LEAVE_OPTIONS);

    const timeToLeaveTranslationMapping = {
        [TIME_TO_LEAVE_OPTIONS.NORMAL]: pricingPlansMessages.timeToLeaveNormal,
        [TIME_TO_LEAVE_OPTIONS.FIXED_HOUR]: pricingPlansMessages.timeToLeaveFixedHour,
    };

    const DurationFormField = React.useCallback(
        ({
            fieldName,
            label,
            disabled,
        }: {
            fieldName: Path<CreatePricingPlanFormSchemaValues>;
            label: string;
            disabled?: boolean;
        }) => {
            const minutesToSeconds = (m: number) => Math.max(0, Math.floor(m)) * 60;
            const secondsToMinutes = (s?: number) => (s == null ? '' : Math.floor(Math.max(0, s) / 60));
            return (
                <FormField
                    control={form.control}
                    name={fieldName}
                    render={({ field }) => (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">{label}</FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Input
                                        type="number"
                                        min={0}
                                        step={1}
                                        disabled={disabled}
                                        defaultValue={formatDuration(field.value as number)}
                                        value={secondsToMinutes(field.value as number) ?? ''}
                                        onChange={(e) => {
                                            const minutes = Number(e.target.value);
                                            if (Number.isNaN(minutes)) {
                                                form.setValue(fieldName, 0, {
                                                    shouldDirty: true,
                                                    shouldValidate: true,
                                                });
                                                return;
                                            }
                                            form.setValue(fieldName, minutesToSeconds(minutes), {
                                                shouldDirty: true,
                                                shouldValidate: true,
                                            });
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    )}
                />
            );
        },
        [form],
    );

    const PriceFormField = React.useCallback(
        ({
            fieldName,
            label,
            disabled,
            value,
            fractionDigits,
            setDisplayedValue,
        }: {
            value: string;
            fieldName: Path<CreatePricingPlanFormSchemaValues>;
            label: string;
            disabled?: boolean;
            fractionDigits?: number;
            setDisplayedValue: (value: string) => void;
        }) => {
            return (
                <FormField
                    control={form.control}
                    name={fieldName}
                    render={() => (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">{label}</FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Input
                                        value={value}
                                        disabled={disabled}
                                        placeholder="0.00"
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const isValid = /^(|0(\.\d{0,2})?|([1-9]\d*)(\.\d{0,2})?)$/.test(value);
                                            if (!isValid || Number(value) > 999999999) return;

                                            setDisplayedValue(value);
                                            const newPrice = Number((Number(value) * 100).toFixed(fractionDigits || 2));
                                            if (isNaN(newPrice)) return form.setValue(fieldName, value);
                                            return form.setValue(fieldName, newPrice);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    )}
                />
            );
        },
        [form],
    );
    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="plan_name"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(generalMessages.name)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input {...field} placeholder={formatMessage(pricingPlansMessages.planName)} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <PriceFormField
                value={displayedValues[PRICE.INITIAL]}
                fieldName={PRICE.INITIAL}
                label={formatMessage(pricingPlansMessages.initialPrice)}
                setDisplayedValue={(value) => setDisplayedValues({ ...displayedValues, [PRICE.INITIAL]: value })}
            />
            <PriceFormField
                value={displayedValues[PRICE.VAT]}
                fieldName={PRICE.VAT}
                label={`${formatMessage(generalPriceMessages.vat)} %`}
                fractionDigits={0}
                setDisplayedValue={(value) => setDisplayedValues({ ...displayedValues, [PRICE.VAT]: value })}
            />
            <DurationFormField fieldName="free_time" label={formatMessage(pricingPlansMessages.freeTime)} />
            <DurationFormField fieldName="time_to_leave" label={formatMessage(pricingPlansMessages.timeToLeave)} />
            <FormField
                control={form.control}
                name="time_to_leave_option"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(pricingPlansMessages.timeToLeaveType)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="NORMAL" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {timeToLeaveOptions.map((option) => (
                                            <SelectItem key={option} value={option}>
                                                {formatMessage(timeToLeaveTranslationMapping[option])}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <DialogTitle>{formatMessage(pricingPlansMessages.firstPeriod)}</DialogTitle>
            <Separator className="mt-2 mb-4" />
            <DurationFormField
                fieldName="first_period.0.duration"
                label={formatMessage(pricingPlansMessages.durationInMinutes)}
            />
            <DurationFormField
                fieldName="first_period.0.price_tick"
                label={formatMessage(pricingPlansMessages.priceTick)}
            />
            <PriceFormField
                value={displayedValues[PRICE.FIRST_PERIOD]}
                fieldName={PRICE.FIRST_PERIOD}
                label={formatMessage(pricingPlansMessages.dueAmount)}
                setDisplayedValue={(value) => setDisplayedValues({ ...displayedValues, [PRICE.FIRST_PERIOD]: value })}
            />
            <DialogTitle>{formatMessage(pricingPlansMessages.secondPeriod)}</DialogTitle>
            <Separator className="mt-2 mb-4" />
            <DurationFormField
                fieldName="second_period.0.duration"
                label={formatMessage(pricingPlansMessages.durationInMinutes)}
            />
            <DurationFormField
                fieldName="second_period.0.price_tick"
                label={formatMessage(pricingPlansMessages.priceTick)}
            />
            <PriceFormField
                value={displayedValues[PRICE.SECOND_PERIOD]}
                fieldName={PRICE.SECOND_PERIOD}
                label={formatMessage(pricingPlansMessages.dueAmount)}
                setDisplayedValue={(value) => setDisplayedValues({ ...displayedValues, [PRICE.SECOND_PERIOD]: value })}
            />
            <DialogTitle>{formatMessage(pricingPlansMessages.thirdPeriod)}</DialogTitle>
            <Separator className="mt-2 mb-4" />
            <DurationFormField
                fieldName="third_period.0.price_tick"
                label={formatMessage(pricingPlansMessages.priceTick)}
            />
            <PriceFormField
                value={displayedValues[PRICE.THIRD_PERIOD]}
                fieldName={PRICE.THIRD_PERIOD}
                label={formatMessage(pricingPlansMessages.dueAmount)}
                setDisplayedValue={(value) => setDisplayedValues({ ...displayedValues, [PRICE.THIRD_PERIOD]: value })}
            />
            <DialogTitle>{formatMessage(pricingPlansMessages.specialPeriod)}</DialogTitle>
            <Separator className="mt-2 mb-4" />
            <FormField
                control={form.control}
                name="special_period.0.in_use"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(pricingPlansMessages.inUse)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    className="size-4"
                                    type="checkbox"
                                    onChange={field.onChange}
                                    checked={special_period[0].in_use}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="special_period.0.period_start"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(membershipMessages.timeFrame)}</FormLabel>
                        <div className="flex w-full gap-4">
                            <FormControlStack>
                                <FormControl>
                                    <Input
                                        defaultValue={formatDuration(field.value)}
                                        type="time"
                                        step={60}
                                        disabled={!special_period[0].in_use}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const [hours, minutes] = value.split(':');
                                            const duration = Number(hours) * 3600 + Number(minutes) * 60;
                                            form.setValue('special_period.0.period_start', duration);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                            <FontAwesomeIcon className="h-10 w-3 text-sm text-gray-400" icon={faMinus} />
                            <FormField
                                control={form.control}
                                name="special_period.0.period_end"
                                render={({ field }) => (
                                    <FormControlStack>
                                        <FormControl>
                                            <Input
                                                defaultValue={formatDuration(field.value)}
                                                type="time"
                                                step={60}
                                                disabled={!special_period[0].in_use}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    const [hours, minutes] = value.split(':');
                                                    const duration = Number(hours) * 3600 + Number(minutes) * 60;
                                                    console.log('duration ==>', duration);
                                                    form.setValue('special_period.0.period_end', duration);
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                )}
                            />
                        </div>
                    </FormItem>
                )}
            />
            <DurationFormField
                fieldName="special_period.0.price_tick"
                label={formatMessage(pricingPlansMessages.priceTick)}
                disabled={!special_period[0].in_use}
            />
            <PriceFormField
                value={displayedValues[PRICE.SPECIAL_PERIOD]}
                fieldName={PRICE.SPECIAL_PERIOD}
                label={formatMessage(pricingPlansMessages.dueAmount)}
                disabled={!special_period[0].in_use}
                setDisplayedValue={(value) => setDisplayedValues({ ...displayedValues, [PRICE.SPECIAL_PERIOD]: value })}
            />
        </Form>
    );
}

export { PricingPlanForm };
